import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { apiTimeOffTypes } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const EMPTY = { name: '', colorHex: '#326496' };

export default function TimeOffTypesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivedFilter, setArchivedFilter] = useState('active');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiTimeOffTypes.list(archivedFilter);
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, colorHex: t.colorHex ?? '#326496' });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const payload = {
        name: form.name.trim(),
        colorHex: form.colorHex || null,
      };
      if (editing) {
        await apiTimeOffTypes.update(editing.id, payload);
      } else {
        await apiTimeOffTypes.create(payload);
      }
      setModalOpen(false);
      await reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (t) => {
    if (!window.confirm(`Archiver le type "${t.name}" ?`)) return;
    await apiTimeOffTypes.archive(t.id);
    await reload();
  };

  const onRestore = async (t) => {
    await apiTimeOffTypes.restore(t.id);
    await reload();
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Types de congés</h1>
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />
        <button type="button" onClick={openCreate} style={{ ...primaryBtn, marginLeft: 'auto' }}>
          <Plus size={14} /> Nouveau type
        </button>
      </div>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Nom</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Couleur</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ padding: 20, color: 'var(--text-muted)' }}>Aucun type.</td>
              </tr>
            ) : (
              items.map((t) => (
                <tr key={t.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--text)' }}>
                    {t.name}
                    {t.archivedAt ? (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(archivé)</span>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          background: t.colorHex ?? 'transparent',
                          border: '1px solid var(--glass-border)',
                        }}
                      />
                      <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>
                        {t.colorHex ?? '—'}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <EntityActions
                      archived={Boolean(t.archivedAt)}
                      onEdit={() => openEdit(t)}
                      onArchive={() => onArchive(t)}
                      onRestore={() => onRestore(t)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Modifier le type' : 'Nouveau type de congé'}
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="submit"
              form="time-off-type-form"
              disabled={submitting || !form.name.trim()}
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              Enregistrer
            </button>
          </>
        }
      >
        <form id="time-off-type-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>Nom</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={64}
              autoFocus
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Couleur d'affichage
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="color"
                value={form.colorHex || '#326496'}
                onChange={(e) => setForm({ ...form, colorHex: e.target.value })}
                style={{
                  width: 48,
                  height: 36,
                  padding: 0,
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              />
              <input
                type="text"
                value={form.colorHex || ''}
                onChange={(e) => setForm({ ...form, colorHex: e.target.value })}
                placeholder="#RRGGBB"
                maxLength={7}
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </div>
          </div>
          {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
        </form>
      </Modal>
    </div>
  );
}
